import './SiderContent.scss';

import { useMenu } from '@refinedev/core';
import type { MenuProps } from 'antd';
import { Menu } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUiContext } from '@/contexts/UiContext';
import { RESOURCES } from '@/Resources';

import AcronexThemedTitle from '../AcronexThemedTitle';
import CountrySelector from '../Header/CountrySelector';

const SiderContent = () => {
  const { t } = useTranslation();
  const { isMediumScreen, setOpenSider } = useUiContext();
  const { hasPermission } = usePermissionContext();
  const { pathname } = useLocation();
  const { menuItems } = useMenu();
  const selectedKey = useMemo(() => {
    const flatItems = menuItems.flatMap(item => {
      if (item.children?.length > 0) {
        return [
          item,
          ...item.children.map(child => ({
            key: child.key,
            route: child.route,
            label: child.label,
            icon: child.icon,
          })),
        ];
      }

      return {
        key: item.key,
        route: item.route,
        label: item.label,
        icon: item.icon,
      };
    });
    const menuItemSelected = flatItems.find(
      menuItem => menuItem.route && pathname.includes(menuItem.route),
    )?.key;

    return menuItemSelected ? [menuItemSelected] : [];
  }, [menuItems, pathname]);
  const filteredMenuItems = useMemo(() => {
    return menuItems.filter(item => {
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;

      if (!hasChildren) {
        // Leaf item: show if any permission on its resource
        return hasPermission('list', item.name as RESOURCES);
      }

      // Parent with children: show if at least one child is allowed
      return item.children?.some(child => hasPermission('list', child.name as RESOURCES));
    });
  }, [hasPermission, menuItems]);
  // Construir el menú con permisos aplicados
  const menuItemsWithPermissions = useMemo<MenuProps['items']>(() => {
    return filteredMenuItems.map(item => {
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      const label = item.label && t(item.label);

      return {
        key: item.key,
        icon: item.icon,
        // Los padres con hijos no navegan; solo expanden/colapsan el submenu.
        label: hasChildren ? label : <Link to={item.route ?? '#'}>{label}</Link>,
        children: hasChildren
          ? item.children
              ?.filter(child => hasPermission('list', child.name as RESOURCES))
              .map(child => ({
                key: child.key,
                icon: child.icon,
                label: <Link to={child.route ?? '#'}>{child.label && t(child.label)}</Link>,
              }))
          : undefined,
      };
    });
  }, [filteredMenuItems, hasPermission, t]);
  const handleMenuClick = useCallback<NonNullable<MenuProps['onClick']>>(() => {
    // En mobile, la navegacion desde un item debe cerrar el drawer sin alternar su estado.
    if (isMediumScreen) {
      setOpenSider(false);
    }
  }, [isMediumScreen, setOpenSider]);

  return (
    <div className="SiderContent">
      <div className="SiderContentHeader">
        <AcronexThemedTitle />
      </div>
      <div className={`SiderContentBody ${isMediumScreen ? 'SiderContentBodyMobile' : ''}`}>
        <Menu
          mode="inline"
          inlineIndent={16}
          className="SiderContentMenu"
          selectedKeys={selectedKey}
          items={menuItemsWithPermissions}
          onClick={handleMenuClick}
        />
      </div>
      {isMediumScreen && (
        <div className="SiderContentFooter">
          <CountrySelector />
        </div>
      )}
    </div>
  );
};

export default SiderContent;
